import { TablePreset, TableStandardConfig } from './table-standard-contracts';

type GenericRow = object;

const defaultConfig = (
  id: string,
  tableClassName: string
): TableStandardConfig<GenericRow> => ({
  id,
  columns: [],
  data: [],
  style: {
    tableClassName,
    striped: true,
    gridlines: false,
    resizableColumns: true,
    rowHover: false
  },
  selection: {
    mode: 'none',
    selected: null
  },
  pagination: {
    enabled: true,
    mode: 'client',
    pageSize: 10,
    pageSizeOptions: [5, 10, 20],
    currentPageReportTemplate:
      'Registros del {first} hasta el {last} de {totalRecords}'
  },
  sorting: {
    enabled: true,
    multiSort: false,
    defaultSort: []
  },
  filtering: {
    enabled: false,
    globalSearch: false,
    globalSearchFields: [],
    globalSearchPlaceholder: 'Buscar...'
  },
  state: {
    loading: false,
    emptyMessage: 'No se encontraron resultados',
    errorMessage: ''
  },
  responsive: {
    mode: 'hybrid',
    minWidth: '72rem',
    scrollHeight: '400px',
    cardBreakpointPx: 768
  },
  a11y: {
    tableAriaLabel: 'Tabla de datos',
    announceSelection: true,
    announcePageChanges: true,
    focusFirstActionOnRowSelect: false
  },
  rowActions: []
});

type ConfigOverride = Partial<TableStandardConfig<GenericRow>>;

const createPreset = (
  key: string,
  description: string,
  id: string,
  tableClassName: string,
  baseOverride: ConfigOverride = {}
): TablePreset<GenericRow> => ({
  key,
  description,
  createConfig: (override) => {
    const base = defaultConfig(id, tableClassName);
    return {
      ...base,
      selection: {
        ...base.selection,
        ...(baseOverride.selection ?? {}),
        ...(override.selection ?? {})
      },
      pagination: {
        ...base.pagination,
        ...(baseOverride.pagination ?? {}),
        ...(override.pagination ?? {})
      },
      sorting: {
        ...base.sorting,
        ...(baseOverride.sorting ?? {}),
        ...(override.sorting ?? {})
      },
      filtering: {
        ...base.filtering,
        ...(baseOverride.filtering ?? {}),
        ...(override.filtering ?? {})
      },
      responsive: {
        ...base.responsive,
        ...(baseOverride.responsive ?? {}),
        ...(override.responsive ?? {})
      },
      ...baseOverride,
      ...override
    };
  }
});

const defaultReportTemplate =
  'Registros del {first} hasta el {last} de {totalRecords}';

const paginationClient = (
  pageSizeOptions: number[] = [10],
  pageSize = 10,
  enabled = false
) => ({
  enabled,
  mode: 'client' as const,
  pageSize,
  pageSizeOptions,
  currentPageReportTemplate: defaultReportTemplate
});

const paginationServer = (
  pageSizeOptions: number[] = [10],
  pageSize = 10,
  enabled = false
) => ({
  enabled,
  mode: 'server' as const,
  pageSize,
  pageSizeOptions,
  currentPageReportTemplate: defaultReportTemplate
});

const sortingDisabled = {
  enabled: false,
  multiSort: false,
  defaultSort: []
};

const filteringDisabled = {
  enabled: false,
  globalSearch: false,
  globalSearchFields: []
};

const filteringSearchEnabled = {
  enabled: true,
  globalSearch: true,
  globalSearchFields: [],
  globalSearchPlaceholder: 'Buscar...'
};

const responsiveHybrid = (scrollHeight: string, minWidth = '72rem') => ({
  mode: 'hybrid' as const,
  minWidth,
  scrollHeight,
  cardBreakpointPx: 768
});

export const informesRechazadosSingleSelectionPreset: TablePreset<GenericRow> =
  createPreset(
    'informes-rechazados-single-selection',
    'Preset para Informes Rechazados con seleccion unica, comportamiento de radio por fila y paginacion servidor (SP_SGS_INFORMES_PENDIENTES).',
    'informes-rechazados',
    'tabla-informes-rechazados',
    {
      selection: { mode: 'single', selected: null },
      pagination: paginationServer([5, 10, 20], 10, true),
      filtering: filteringDisabled
    }
  );

export const orientacionesNoProgramadasPreset: TablePreset<GenericRow> =
  createPreset(
    'orientaciones-no-programadas',
    'Preset para flujo orientaciones no programadas con modo responsive hibrido y acciones por fila.',
    'orientaciones-no-programadas',
    'tabla-orientaciones-no-programadas',
    {
      selection: { mode: 'none', selected: null },
      pagination: paginationServer([10, 20, 50], 10, true)
    }
  );

export const busquedaDialogPreset: TablePreset<GenericRow> = createPreset(
  'busqueda-dialog',
  'Preset para dialogos de busqueda reutilizables con seleccion unica y paginacion ligera.',
  'busqueda-dialog',
  'tabla-busqueda-generica',
  {
    selection: { mode: 'single', selected: null },
    pagination: paginationClient([10]),
    filtering: filteringSearchEnabled
  }
);

export const massiveResultPreset: TablePreset<GenericRow> = createPreset(
  'massive-result',
  'Preset para resultados de procesos masivos, prioriza legibilidad y trazabilidad por fila.',
  'massive-result',
  'tabla-resultados',
  {
    selection: { mode: 'none', selected: null },
    sorting: sortingDisabled,
    filtering: filteringDisabled
  }
);

export const historialGestionesPreset: TablePreset<GenericRow> = createPreset(
  'historial-gestiones',
  'Preset para historial de gestiones con foco en trazabilidad temporal y lectura en desktop/mobile.',
  'historial-gestiones',
  'tabla-historial-cumplimientos',
  {
    selection: { mode: 'none', selected: null },
    pagination: paginationClient([5, 10, 25], 5, true),
    sorting: sortingDisabled,
    filtering: filteringDisabled,
    responsive: responsiveHybrid('450px')
  }
);

export const cancelacionSitioDetallePreset: TablePreset<GenericRow> =
  createPreset(
    'cancelacion-sitio-detalle',
    'Preset para tablas de detalle de cancelacion en sitio, con foco en trazabilidad y seleccion controlada.',
    'cancelacion-sitio-detalle',
    'tabla-cambio-orientacion',
    {
      selection: { mode: 'none', selected: null },
      pagination: paginationClient([10, 20]),
      sorting: sortingDisabled,
      filtering: filteringDisabled,
      responsive: responsiveHybrid('420px')
    }
  );

export const cancelacionSitioListadoPreset: TablePreset<GenericRow> =
  createPreset(
    'cancelacion-sitio-listado',
    'Preset para listado principal de encabezados de cancelacion en sitio.',
    'cancelacion-sitio-listado',
    'tabla-cambio-orientacion',
    {
      selection: { mode: 'none', selected: null },
      pagination: paginationClient([10, 20]),
      sorting: sortingDisabled,
      filtering: filteringDisabled,
      responsive: responsiveHybrid('420px')
    }
  );

export const personasListadoPreset: TablePreset<GenericRow> = createPreset(
  'personas-listado',
  'Preset para listado de personas con foco en consulta, legibilidad y acciones de edicion.',
  'personas-listado',
  'tabla-cambio-orientacion',
  {
    selection: { mode: 'none', selected: null },
    pagination: paginationClient([10, 20, 50]),
    sorting: sortingDisabled,
    filtering: filteringDisabled,
    responsive: responsiveHybrid('400px')
  }
);

export const contactosPersonaPreset: TablePreset<GenericRow> = createPreset(
  'contactos-persona',
  'Preset para listado de contactos asociado a persona, con foco en legibilidad y acciones compactas.',
  'contactos-persona',
  'tabla-cambio-orientacion',
  {
    selection: { mode: 'none', selected: null },
    pagination: paginationClient([5, 10, 20]),
    sorting: sortingDisabled,
    filtering: filteringDisabled,
    responsive: responsiveHybrid('400px')
  }
);

export const direccionesPersonaPreset: TablePreset<GenericRow> = createPreset(
  'direcciones-persona',
  'Preset para listado de direcciones por persona con paginacion y acciones de soporte.',
  'direcciones-persona',
  'tabla-cambio-orientacion',
  {
    selection: { mode: 'none', selected: null },
    pagination: {
      enabled: true,
      mode: 'client',
      pageSize: 10,
      pageSizeOptions: [5, 10, 25],
      currentPageReportTemplate: 'Mostrando {first} a {last} de {totalRecords} direcciones'
    },
    sorting: sortingDisabled,
    filtering: filteringDisabled,
    responsive: responsiveHybrid('420px')
  }
);

export const busquedaCentroTrabajoPreset: TablePreset<GenericRow> =
  createPreset(
    'busqueda-centro-trabajo',
    'Preset para dialogo de busqueda de centros de trabajo con foco en seleccion y legibilidad.',
    'busqueda-centro-trabajo',
    'tabla-busqueda-centro-trabajo',
    {
      selection: { mode: 'single', selected: null },
      pagination: paginationClient([10]),
      sorting: sortingDisabled,
      filtering: filteringSearchEnabled,
      responsive: responsiveHybrid('250px')
    }
  );

export const busquedaMunicipioPreset: TablePreset<GenericRow> = createPreset(
  'busqueda-municipio',
  'Preset para dialogo de busqueda de municipios con foco en trazabilidad de codigos y seleccion rapida.',
  'busqueda-municipio',
  'tabla-busqueda-municipio',
  {
    selection: { mode: 'none', selected: null },
    pagination: paginationClient([10]),
    sorting: sortingDisabled,
    filtering: filteringSearchEnabled,
    responsive: responsiveHybrid('300px')
  }
);

export const busquedaAsesorPreset: TablePreset<GenericRow> = createPreset(
  'busqueda-asesor',
  'Preset para dialogo de busqueda de asesores con foco en legibilidad y seleccion por accion.',
  'busqueda-asesor',
  'tabla-busqueda-asesor',
  {
    selection: { mode: 'none', selected: null },
    pagination: paginationClient([10]),
    sorting: sortingDisabled,
    filtering: filteringSearchEnabled,
    responsive: responsiveHybrid('300px')
  }
);

export const busquedaClientePreset: TablePreset<GenericRow> = createPreset(
  'busqueda-cliente',
  'Preset para dialogo de busqueda de clientes con seleccion unica y tabla de resultados orientada a filtros.',
  'busqueda-cliente',
  'tabla-busqueda-cliente',
  {
    selection: { mode: 'single', selected: null },
    pagination: paginationClient([10]),
    sorting: sortingDisabled,
    filtering: filteringSearchEnabled,
    responsive: responsiveHybrid('250px')
  }
);

export const busquedaProductoPreset: TablePreset<GenericRow> = createPreset(
  'busqueda-producto',
  'Preset para dialogo de busqueda de productos con seleccion unica y navegacion paginada. ',
  'busqueda-producto',
  'tabla-busqueda-producto',
  {
    selection: { mode: 'single', selected: null },
    pagination: paginationClient([10]),
    sorting: sortingDisabled,
    filtering: filteringSearchEnabled,
    responsive: responsiveHybrid('300px')
  }
);

export const busquedaTareaPreset: TablePreset<GenericRow> = createPreset(
  'busqueda-tarea',
  'Preset para dialogo de busqueda de tareas con seleccion unica y trazabilidad de descripcion.',
  'busqueda-tarea',
  'tabla-busqueda-tarea',
  {
    selection: { mode: 'single', selected: null },
    pagination: paginationClient([10]),
    sorting: sortingDisabled,
    filtering: filteringSearchEnabled,
    responsive: responsiveHybrid('300px')
  }
);

export const busquedaProfesionalPreset: TablePreset<GenericRow> =
  createPreset(
    'busqueda-profesional',
    'Preset para dialogo de busqueda de profesionales con paginacion servidor y seleccion unica.',
    'busqueda-profesional',
    'tabla-busqueda-profesional',
    {
      selection: { mode: 'single', selected: null },
      pagination: paginationServer([10]),
      sorting: sortingDisabled,
      filtering: filteringSearchEnabled,
      responsive: responsiveHybrid('300px')
    }
  );

export const busquedaUnidadNegocioPreset: TablePreset<GenericRow> =
  createPreset(
    'busqueda-unidad-negocio',
    'Preset para dialogo de busqueda de unidad de negocio con seleccion unica y lectura compacta.',
    'busqueda-unidad-negocio',
    'tabla-busqueda-unidad-negocio',
    {
      selection: { mode: 'single', selected: null },
      pagination: paginationClient([10]),
      sorting: sortingDisabled,
      filtering: filteringSearchEnabled,
      responsive: responsiveHybrid('250px')
    }
  );

export const busquedaRespuestaPreset: TablePreset<GenericRow> = createPreset(
  'busqueda-respuesta',
  'Preset para dialogo de busqueda de respuestas con seleccion unica y paginacion manual.',
  'busqueda-respuesta',
  'tabla-busqueda-respuesta',
  {
    selection: { mode: 'single', selected: null },
    pagination: paginationServer([10]),
    sorting: sortingDisabled,
    filtering: filteringSearchEnabled,
    responsive: responsiveHybrid('300px')
  }
);

export const busquedaTipoAccesoPreset: TablePreset<GenericRow> =
  createPreset(
    'busqueda-tipo-acceso',
    'Preset para dialogo de busqueda de tipo de acceso con seleccion unica y paginacion manual.',
    'busqueda-tipo-acceso',
    'tabla-busqueda-tipo-acceso',
    {
      selection: { mode: 'single', selected: null },
      pagination: paginationServer([10]),
      sorting: sortingDisabled,
      filtering: filteringSearchEnabled,
      responsive: responsiveHybrid('300px')
    }
  );

export const pmAsesoresTablePreset: TablePreset<GenericRow> = createPreset(
  'pm-asesores-table',
  'Preset para tabla de consultores seleccionados en programacion masiva.',
  'pm-asesores-table',
  'tabla-pm-asesores',
  {
    selection: { mode: 'none', selected: null },
    pagination: paginationClient([10]),
    sorting: sortingDisabled,
    filtering: filteringDisabled,
    responsive: responsiveHybrid('260px')
  }
);

export const pmBuscarDialogPreset: TablePreset<GenericRow> = createPreset(
  'pm-buscar-dialog',
  'Preset para tabla de seleccion de consultores en dialogo de programacion masiva.',
  'pm-buscar-dialog',
  'tabla-pm-buscar',
  {
    selection: { mode: 'none', selected: null },
    pagination: paginationClient([10]),
    sorting: sortingDisabled,
    filtering: filteringDisabled,
    responsive: responsiveHybrid('320px')
  }
);

export const pmErroresDialogPreset: TablePreset<GenericRow> = createPreset(
  'pm-errores-dialog',
  'Preset para tabla de resultados y errores de programacion masiva.',
  'pm-errores-dialog',
  'tabla-pm-errores',
  {
    selection: { mode: 'none', selected: null },
    pagination: paginationClient([10]),
    sorting: sortingDisabled,
    filtering: filteringDisabled,
    responsive: responsiveHybrid('320px')
  }
);

export const buscadorActividadEconomicaPreset: TablePreset<GenericRow> =
  createPreset(
    'buscador-actividad-economica',
    'Preset para buscador de actividades economicas con tabla de seleccion y paginacion manual.',
    'buscador-actividad-economica',
    'tabla-buscador-actividad-economica',
    {
      selection: { mode: 'none', selected: null },
      pagination: paginationServer([10]),
      sorting: sortingDisabled,
      filtering: filteringSearchEnabled,
      responsive: responsiveHybrid('300px')
    }
  );

export const buscadorAseguradoraPreset: TablePreset<GenericRow> =
  createPreset(
    'buscador-aseguradora',
    'Preset para buscador de aseguradoras con seleccion unica y paginacion backend.',
    'buscador-aseguradora',
    'tabla-buscador-aseguradora',
    {
      selection: { mode: 'single', selected: null },
      pagination: paginationServer([10]),
      sorting: sortingDisabled,
      filtering: filteringSearchEnabled,
      responsive: responsiveHybrid('250px')
    }
  );

export const buscadorContactosPreset: TablePreset<GenericRow> = createPreset(
  'buscador-contactos',
  'Preset para buscador de contactos con acciones de seleccion y edicion.',
  'buscador-contactos',
  'tabla-buscador-contactos',
  {
    selection: { mode: 'none', selected: null },
    pagination: paginationServer([5, 10, 20], 10, true),
    sorting: sortingDisabled,
    filtering: filteringSearchEnabled,
    responsive: responsiveHybrid('300px')
  }
);

export const buscadorMunicipioPreset: TablePreset<GenericRow> = createPreset(
  'buscador-municipio',
  'Preset para buscador de municipios con seleccion por accion y paginacion manual.',
  'buscador-municipio',
  'tabla-buscador-municipio',
  {
    selection: { mode: 'none', selected: null },
    pagination: paginationServer([10]),
    sorting: sortingDisabled,
    filtering: filteringSearchEnabled,
    responsive: responsiveHybrid('300px')
  }
);

export const buscadorPoblacionPreset: TablePreset<GenericRow> = createPreset(
  'buscador-poblacion',
  'Preset para buscador de poblaciones con paginacion lazy y seleccion directa.',
  'buscador-poblacion',
  'tabla-buscador-poblacion',
  {
    selection: { mode: 'none', selected: null },
    pagination: paginationServer([5, 10, 25], 10, true),
    sorting: sortingDisabled,
    filtering: filteringSearchEnabled,
    responsive: responsiveHybrid('300px')
  }
);

export const buscadorUnidadAdministrativaPreset: TablePreset<GenericRow> =
  createPreset(
    'buscador-unidad-administrativa',
    'Preset para buscador de unidades administrativas con seleccion unica y paginacion backend.',
    'buscador-unidad-administrativa',
    'tabla-buscador-unidad-administrativa',
    {
      selection: { mode: 'single', selected: null },
      pagination: paginationServer([10]),
      sorting: sortingDisabled,
      filtering: filteringSearchEnabled,
      responsive: responsiveHybrid('250px')
    }
  );

export const cumplimientosMasivosResultadoPreset: TablePreset<GenericRow> =
  createPreset(
    'cumplimientos-masivos-resultado',
    'Preset para tabla de resultados de cargue masivo de cumplimientos sin OC.',
    'cumplimientos-masivos-resultado',
    'tabla-cumplimientos-masivos',
    {
      selection: { mode: 'none', selected: null },
      pagination: paginationClient([10]),
      sorting: sortingDisabled,
      filtering: filteringDisabled,
      responsive: responsiveHybrid('250px')
    }
  );

export const gestionesMasivasResultadoPreset: TablePreset<GenericRow> =
  createPreset(
    'gestiones-masivas-resultado',
    'Preset para tabla de resultados de cargue masivo de gestiones sin OC.',
    'gestiones-masivas-resultado',
    'tabla-gestiones-masivas',
    {
      selection: { mode: 'none', selected: null },
      pagination: paginationClient([10]),
      sorting: sortingDisabled,
      filtering: filteringDisabled,
      responsive: responsiveHybrid('250px')
    }
  );

export const cancelarCitasMasivasResultadoPreset: TablePreset<GenericRow> =
  createPreset(
    'cancelar-citas-masivas-resultado',
    'Preset para tabla de resultados de cancelacion masiva de citas.',
    'cancelar-citas-masivas-resultado',
    'tabla-resultados',
    {
      selection: { mode: 'none', selected: null },
      pagination: paginationClient([10]),
      sorting: sortingDisabled,
      filtering: filteringDisabled,
      responsive: responsiveHybrid('200px')
    }
  );

export const crearCitasMasivasResultadoPreset: TablePreset<GenericRow> =
  createPreset(
    'crear-citas-masivas-resultado',
    'Preset para tabla de resultados de creacion masiva de citas.',
    'crear-citas-masivas-resultado',
    'tabla-resultados',
    {
      selection: { mode: 'none', selected: null },
      pagination: paginationClient([10]),
      sorting: sortingDisabled,
      filtering: filteringDisabled,
      responsive: responsiveHybrid('250px')
    }
  );

export const seleccionarAsesorDialogPreset: TablePreset<GenericRow> =
  createPreset(
    'seleccionar-asesor-dialog',
    'Preset para dialogo de seleccion de asesor en programacion de ordenes de servicio.',
    'seleccionar-asesor-dialog',
    'tabla-seleccionar-asesor',
    {
      selection: { mode: 'single', selected: null },
      pagination: paginationClient([10]),
      sorting: sortingDisabled,
      filtering: filteringDisabled,
      responsive: responsiveHybrid('400px')
    }
  );

export const ordenesServicioListadoPreset: TablePreset<GenericRow> =
  createPreset(
    'ordenes-servicio-listado',
    'Preset para listado principal de ordenes de servicio con paginacion externa.',
    'ordenes-servicio-listado',
    'tabla-ordenes-servicio',
    {
      selection: { mode: 'none', selected: null },
      pagination: paginationServer([10, 25, 50]),
      sorting: sortingDisabled,
      filtering: {
        enabled: true,
        globalSearch: true,
        globalSearchFields: [
          'nmconsecutivoManualOrden',
          'nombreBeneficiario',
          'dscentroTrabajo',
          'dsproducto',
          'tareaCliente',
          'dsclasificacion',
          'dsabrevClasif'
        ],
        globalSearchPlaceholder: 'Buscar...'
      },
      responsive: responsiveHybrid('550px', '92rem')
    }
  );

export const programarServicioProgramacionesPreset: TablePreset<GenericRow> =
  createPreset(
    'programar-servicio-programaciones',
    'Preset para tabla de programaciones existentes en dialogo Programar Servicio.',
    'programar-servicio-programaciones',
    'tabla-programar-servicio',
    {
      selection: { mode: 'none', selected: null },
      pagination: {
        enabled: true,
        mode: 'client',
        pageSize: 5,
        pageSizeOptions: [5, 10, 20],
        currentPageReportTemplate: '{first} - {last} de {totalRecords}'
      },
      sorting: sortingDisabled,
      filtering: filteringDisabled,
      responsive: responsiveHybrid('auto', '92rem')
    }
  );

export const programarServicioTareasPreset: TablePreset<GenericRow> =
  createPreset(
    'programar-servicio-tareas',
    'Preset para tabla de otras tareas del cliente en dialogo Programar Servicio.',
    'programar-servicio-tareas',
    'tabla-programar-servicio',
    {
      selection: { mode: 'none', selected: null },
      pagination: {
        enabled: true,
        mode: 'client',
        pageSize: 5,
        pageSizeOptions: [5, 10, 20],
        currentPageReportTemplate: '{first} - {last} de {totalRecords}'
      },
      sorting: sortingDisabled,
      filtering: filteringDisabled,
      responsive: responsiveHybrid('auto', '96rem')
    }
  );

export const contactosTrabajoDialogPreset: TablePreset<GenericRow> =
  createPreset(
    'contactos-trabajo-dialog',
    'Preset para tabla de contactos de centro de trabajo en dialogo de agenda.',
    'contactos-trabajo-dialog',
    'tabla-contactos-trabajo',
    {
      selection: { mode: 'none', selected: null },
      pagination: paginationClient([10, 25, 50]),
      sorting: sortingDisabled,
      filtering: filteringDisabled,
      responsive: responsiveHybrid('320px')
    }
  );

export const historialCumplimientosDialogPreset: TablePreset<GenericRow> =
  createPreset(
    'historial-cumplimientos-dialog',
    'Preset para tabla de historial de cumplimientos en dialogo de consulta.',
    'historial-cumplimientos-dialog',
    'tabla-historial-cumplimientos',
    {
      selection: { mode: 'none', selected: null },
      pagination: paginationClient([10, 25, 50]),
      sorting: sortingDisabled,
      filtering: filteringDisabled,
      responsive: responsiveHybrid('360px', '92rem')
    }
  );

export const tablaProgramacionesPreset: TablePreset<GenericRow> = createPreset(
  'tabla-programaciones',
  'Preset para tabla de programaciones pendientes en modulo de cumplimientos.',
  'tabla-programaciones',
  'tabla-cumplimientos-programados',
  {
    selection: { mode: 'none', selected: null },
    pagination: paginationServer([10, 25, 50]),
    sorting: sortingDisabled,
    filtering: filteringDisabled,
    responsive: responsiveHybrid('auto', '92rem')
  }
);

export const cambioOrientacionListadoPreset: TablePreset<GenericRow> =
  createPreset(
    'cambio-orientacion-listado',
    'Preset para listado de orientaciones en modulo de cambio de orientacion.',
    'cambio-orientacion-listado',
    'tabla-cambio-orientacion',
    {
      selection: { mode: 'multiple', selected: [], dataKey: 'nmconsecutivo' },
      pagination: paginationServer([10, 20, 50, 100]),
      sorting: sortingDisabled,
      filtering: filteringDisabled,
      responsive: responsiveHybrid('auto', '92rem')
    }
  );

export const devolucionCumplimientosPreset: TablePreset<GenericRow> =
  createPreset(
    'devolucion-cumplimientos-listado',
    'Preset para tabla de devolucion de cumplimientos con seleccion multiple.',
    'devolucion-cumplimientos-listado',
    'tabla-devolucion-cumplimientos',
    {
      selection: { mode: 'multiple', selected: [], dataKey: 'rowKey' },
      pagination: paginationServer([10, 25, 50]),
      sorting: sortingDisabled,
      filtering: filteringDisabled,
      responsive: responsiveHybrid('400px', '119rem')
    }
  );

export const aprobarRechazarInformesPreset: TablePreset<GenericRow> =
  createPreset(
    'aprobar-rechazar-informes',
    'Preset para listado de informes enviados preaprobados con seleccion multiple y paginacion por cursor.',
    'aprobar-rechazar-informes',
    'tabla-aprobar-rechazar-informes',
    {
      selection: { mode: 'multiple', selected: [], dataKey: 'nmconsecutivoinforme' },
      pagination: paginationServer([10]),
      sorting: sortingDisabled,
      filtering: filteringDisabled,
      responsive: responsiveHybrid('480px', '72rem')
    }
  );
